import { useState, useEffect, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface RoleCache {
  userId: string;
  isAdmin: boolean;
  isModerator: boolean;
  timestamp: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY = 'admin_role_cache';

function readCache(userId: string): RoleCache | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: RoleCache = JSON.parse(raw);
    if (cached.userId === userId && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached;
    }
  } catch {}
  return null;
}

function writeCache(entry: RoleCache) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry)); } catch {}
}

export function useAdminAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isModerator, setIsModerator] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  // Guards: in-flight promise + completed user id
  const inflightRef = useRef<Promise<void> | null>(null);
  const verifiedUserIdRef = useRef<string | null>(null);

  const applyRoles = useCallback((userId: string, admin: boolean, moderator: boolean) => {
    setIsAdmin(admin);
    setIsModerator(moderator);
    verifiedUserIdRef.current = userId;
    writeCache({ userId, isAdmin: admin, isModerator: moderator, timestamp: Date.now() });
  }, []);

  const checkRoles = useCallback((userId: string): Promise<void> => {
    // Already verified for this user — skip
    if (verifiedUserIdRef.current === userId) {
      setIsLoading(false);
      return Promise.resolve();
    }

    // Check sessionStorage cache
    const cached = readCache(userId);
    if (cached) {
      applyRoles(userId, cached.isAdmin, cached.isModerator);
      setIsLoading(false);
      return Promise.resolve();
    }

    // Reuse in-flight request if one exists
    if (inflightRef.current) {
      return inflightRef.current;
    }

    const promise = (async () => {
      try {
        const { data: roleData, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .single();

        if (error) {
          console.error('Error checking role:', error);
          setIsAdmin(false);
          setIsModerator(false);
        } else {
          const role = roleData?.role;
          applyRoles(userId, role === 'admin', role === 'moderator');
        }
      } catch (err) {
        console.error('Error checking roles:', err);
        setIsAdmin(false);
        setIsModerator(false);
      } finally {
        inflightRef.current = null;
        setIsLoading(false);
      }
    })();

    inflightRef.current = promise;
    return promise;
  }, [applyRoles]);

  useEffect(() => {
    let initialSessionHandled = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Skip the INITIAL_SESSION event — we handle it via getSession below
        if (event === 'INITIAL_SESSION') return;

        // Only react to meaningful auth changes
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          setSession(newSession);
          setUser(newSession?.user ?? null);

          if (event === 'SIGNED_OUT') {
            setIsAdmin(false);
            setIsModerator(false);
            verifiedUserIdRef.current = null;
            setIsLoading(false);
            try { sessionStorage.removeItem(CACHE_KEY); } catch {}
            return;
          }

          if (newSession?.user) {
            // Already verified or in-flight for this user — skip
            if (verifiedUserIdRef.current === newSession.user.id || inflightRef.current) {
              return;
            }
            checkRoles(newSession.user.id);
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (initialSessionHandled) return;
      initialSessionHandled = true;

      setSession(existingSession);
      setUser(existingSession?.user ?? null);

      if (existingSession?.user) {
        checkRoles(existingSession.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkRoles]);

  const signIn = async (email: string, password: string) => {
    // Clear stale cache on new sign-in attempt
    verifiedUserIdRef.current = null;
    try { sessionStorage.removeItem(CACHE_KEY); } catch {}
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsModerator(false);
      verifiedUserIdRef.current = null;
      try { sessionStorage.removeItem(CACHE_KEY); } catch {}
    }
    return { error };
  };

  // canAccessAdmin: true if user is admin OR moderator
  const canAccessAdmin = isAdmin || isModerator;

  return {
    user,
    session,
    isAdmin,
    isModerator,
    canAccessAdmin,
    isLoading,
    signIn,
    signOut,
  };
}
